import React, { useMemo } from 'react'
import { Drawer, Slider, InputNumber, Button, Space, Typography, Divider, Grid } from 'antd'
import { useWizardStore } from '../store'

const { Text, Title } = Typography

const presetPercents = [3, 5, 8, 10, 12]

function toTwoDecimals(value) {
  const num = Number(value) || 0
  return Math.round(num * 100) / 100
}

export default function PensionQuickAdjustDrawer({ open, onClose }) {
  const { useBreakpoint } = Grid
  const screens = useBreakpoint()
  const inputs = useWizardStore((s) => s.inputs)
  const setInputs = useWizardStore((s) => s.setInputs)

  const baseSalary = Number(inputs.baseSalary) || 0
  const percent = Number(inputs.employeePensionContribution) || 0

  const annualAmount = useMemo(() => toTwoDecimals((percent / 100) * baseSalary), [percent, baseSalary])
  const monthlyAmount = useMemo(() => toTwoDecimals(annualAmount / 12), [annualAmount])

  const updatePercent = (p) => {
    const safe = Math.max(0, Math.min(100, Number(p) || 0))
    setInputs({
      employeePensionContribution: toTwoDecimals(safe),
      employeePensionContributionMode: 'percentage',
    })
  }

  const isMobile = !screens.md

  return (
    <Drawer
      title={<Title level={5} style={{ margin: 0 }}>Adjust Employee Pension %</Title>}
      placement={isMobile ? 'bottom' : 'right'}
      height={isMobile ? '65%' : undefined}
      onClose={onClose}
      open={open}
      width={isMobile ? '100%' : 360}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">Use the slider or enter a value to quickly explore contributions. Results update instantly.</Text>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Slider
              min={0}
              max={100}
              step={0.1}
              value={percent}
              onChange={updatePercent}
            />
          </div>
          <InputNumber
            style={{ width: 110 }}
            min={0}
            max={100}
            step={0.1}
            value={percent}
            onChange={updatePercent}
            precision={2}
            stringMode
            formatter={(v) => `${v}%`}
            parser={(v) => (v || '').replace('%', '')}
          />
        </div>

        <Space wrap>
          {presetPercents.map((p) => (
            <Button key={p} onClick={() => updatePercent(p)} type={percent === p ? 'primary' : 'default'}>
              {p}%
            </Button>
          ))}
        </Space>

        <Divider style={{ margin: '8px 0' }} />

        <div className="bg-gray-50 border rounded p-3">
          <Text strong>Preview</Text>
          <div className="mt-1 text-sm text-gray-700">
            {baseSalary > 0 ? (
              <>
                <div>Annual: £{annualAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div>Monthly: £{monthlyAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className="text-gray-500 mt-1">Based on base salary £{baseSalary.toLocaleString()}</div>
              </>
            ) : (
              <div className="text-gray-500">Enter a base salary to see £ preview</div>
            )}
          </div>
        </div>

        <div className="text-xs text-gray-500">
          This adjusts only the employee contribution %. Employer contribution settings remain unchanged.
        </div>

        <div className="flex gap-2 justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </Space>
    </Drawer>
  )
}


